import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { Order, StatusHistoryEntry } from './useOrderQuery';

export interface OrderFilters {
  organizationId: string | null;
  status?: string | null;
  branchId?: string | null;
  dateFrom?: string | null;
  dateTo?: string | null;
  search?: string | null;
  sortCol?: string;
  sortDir?: 'asc' | 'desc';
  page?: number;
}

interface UseAdminOrdersListResult {
  orders: Order[];
  totalCount: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

const PAGE_SIZE = 20;

export function useAdminOrdersList(filters: OrderFilters): UseAdminOrdersListResult {
  const {
    organizationId,
    status = null,
    branchId = null,
    dateFrom = null,
    dateTo = null,
    search = null,
    sortCol = 'created_at',
    sortDir = 'desc',
    page = 0,
  } = filters;

  const [orders, setOrders] = useState<Order[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const refetch = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  useEffect(() => {
    if (!organizationId) {
      setOrders([]);
      setTotalCount(0);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchOrders() {
      setIsLoading(true);
      setError(null);

      try {
        const start = page * PAGE_SIZE;
        const end = start + PAGE_SIZE - 1;

        let query = supabase
          .from('orders')
          .select(`
            *,
            profiles(*),
            status_config(*),
            branches(name)
          `, { count: 'exact' })
          .eq('branches.organization_id', organizationId);

        // Apply status filter
        if (status) {
          query = query.eq('status_config.name', status);
        }

        // Apply branch filter
        if (branchId) {
          query = query.eq('branch_id', branchId);
        }

        // Apply date range filters
        if (dateFrom) {
          query = query.filter('created_at', 'gte', `${dateFrom}T00:00:00`);
        }
        if (dateTo) {
          query = query.filter('created_at', 'lte', `${dateTo}T23:59:59`);
        }

        // Apply search filter (case-insensitive on multiple fields)
        if (search) {
          query = query.or(
            `customer_name.ilike.%${search}%,release_code.ilike.%${search}%,device.ilike.%${search}%`
          );
        }

        // Apply sorting
        query = query.order(sortCol, { ascending: sortDir === 'asc' });

        // Apply pagination
        const { data, error: fetchError, count } = await query.range(start, end);

        if (fetchError) {
          if (!cancelled) {
            setError(fetchError.message);
            setOrders([]);
            setTotalCount(0);
          }
          return;
        }

        if (!cancelled) {
          setOrders((data as Order[]) || []);
          setTotalCount(count || 0);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError('Failed to fetch orders');
          setOrders([]);
          setTotalCount(0);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchOrders();

    return () => {
      cancelled = true;
    };
  }, [organizationId, status, branchId, dateFrom, dateTo, search, sortCol, sortDir, page, refreshTrigger]);

  return {
    orders,
    totalCount,
    isLoading,
    error,
    refetch,
  };
}
